import type { Analytix } from '~/shared/analytix'
import type { AnalytixDispatcher } from './dispatcher'
import type { AnyAnalytixEvent } from './event'

import { logger } from '../lib'

/**
 * Drives the async dispatch loop, consuming events from an
 * {@link AnalytixDispatcher} and sending them one by one.
 *
 * Separates the dispatch lifecycle (start/stop) from the dispatcher itself,
 * which only concerns itself with ordering and readiness. The emitter owns
 * the running state and error boundaries around {@link AnalytixEvent.send}.
 *
 * @example
 * ```ts
 * const emitter = new AnalytixEmitter(dispatcher)
 * emitter.start()
 * // ...
 * emitter.stop()
 * ```
 */
export class AnalytixEmitter {
  private events: AsyncGenerator<AnyAnalytixEvent> | null = null
  private running = false
  private dispatcher: AnalytixDispatcher

  /**
   * @param dispatcher - The dispatcher to consume events from.
   */
  constructor(dispatcher: AnalytixDispatcher) {
    this.dispatcher = dispatcher
  }

  /**
   * Starts the emitter, opening a dispatch generator and beginning the run
   * loop.
   *
   * No-ops if the emitter is already running. The run loop is fire-and-forget
   * — errors are caught internally and logged rather than propagated to the
   * caller.
   *
   * @param analytix - The Analytix instance to pass to events when sending.
   */
  start(analytix: Analytix): void {
    if (this.running) return

    this.running = true
    this.events = this.dispatcher.dispatch()
    this.run(analytix)
  }

  /**
   * Stops the emitter by signalling the dispatch generator to finish and
   * clearing internal state.
   *
   * Does not await the run loop completing — if `event.send()` is in progress
   * when `stop()` is called, it will finish before the loop exits. Restart
   * immediately after `stop()` is therefore not recommended; await the current
   * send to complete first if overlap is a concern.
   *
   * No-ops if the emitter is not running.
   */
  stop(): void {
    if (!this.running || !this.events) return

    this.running = false
    this.events.return(undefined)
    this.events = null
  }

  /**
   * Internal run loop. Iterates the dispatch generator and calls
   * {@link AnalytixEvent.send} for each event.
   *
   * @param analytix - The Analytix instance to pass to events when sending.
   */
  private async run(analytix: Analytix): Promise<void> {
    logger.info('emitter started')
    try {
      for await (const event of this.events!) {
        // Guard against stop() being called between yields, for the case where
        // .return() has not yet propagated to the for-await loop.
        if (!this.running) break

        try {
          await event.send(analytix)
        } catch (error) {
          logger.error('failed to emit event:', error, event)
        }
      }
    } catch (error) {
      logger.error('emitter error:', error)
    } finally {
      this.running = false
      this.events = null
      logger.info('emitter stopped')
    }
  }
}
